import { useEffect, useRef } from "react";

/**
 * Custom hook for synchronized scrolling between editor and preview panes.
 * @param {React.RefObject} wrapperRef - Ref to the editor wrapper element
 * @param {boolean} isEnabled - Whether scroll sync should be active
 */
const useScrollSync = (wrapperRef, isEnabled) => {
  const isScrollingSyncRef = useRef(false);

  useEffect(() => {
    if (!isEnabled) return;

    let editorArea = null;
    let previewArea = null;
    let handleEditorScroll = null;
    let handlePreviewScroll = null;

    const setupScrollSync = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      editorArea = wrapper.querySelector(".w-md-editor-text-input");
      previewArea = wrapper.querySelector(".w-md-editor-preview");

      if (!editorArea || !previewArea) return;

      const syncScroll = (source, target) => {
        if (isScrollingSyncRef.current) return;
        isScrollingSyncRef.current = true;

        const sourceScrollHeight = source.scrollHeight - source.clientHeight;
        const targetScrollHeight = target.scrollHeight - target.clientHeight;

        if (sourceScrollHeight > 0 && targetScrollHeight > 0) {
          const scrollRatio = source.scrollTop / sourceScrollHeight;
          target.scrollTop = scrollRatio * targetScrollHeight;
        }

        requestAnimationFrame(() => {
          isScrollingSyncRef.current = false;
        });
      };

      handleEditorScroll = () => syncScroll(editorArea, previewArea);
      handlePreviewScroll = () => syncScroll(previewArea, editorArea);

      editorArea.addEventListener("scroll", handleEditorScroll);
      previewArea.addEventListener("scroll", handlePreviewScroll);
    };

    const timeoutId = setTimeout(setupScrollSync, 200);

    return () => {
      clearTimeout(timeoutId);
      if (editorArea && handleEditorScroll) {
        editorArea.removeEventListener("scroll", handleEditorScroll);
      }
      if (previewArea && handlePreviewScroll) {
        previewArea.removeEventListener("scroll", handlePreviewScroll);
      }
    };
  }, [wrapperRef, isEnabled]);
};

export default useScrollSync;
